import { Icon } from '@polichat/flamboyant/dist';
import TagSelector from './TagSelector';
import { TriggerSuggestionService } from 'src/services/triggerSuggestion/triggerSuggestionService';
import { useSelector, useDispatch } from 'react-redux';
import { isMobile } from 'react-device-detect';
import * as ChatActions from 'src/store/modules/chat/actions';

function WhatsappMarketingContent({
  chat,
  channels,
  setChannels,
  suggestedContacts,
  setSuggestedContacts,
  contactsLoading,
  setContactsLoading,
  contactsPagination,
  setContactsPagination,
  selectedContacts,
  setSelectedContacts,
  messageConfig,
  setMessageConfig,
  currentChannelId,
  setCurrentChannelId,
  fetchSuggestedContacts,
  selectAllContacts,
  deselectAllContacts,
  handleScroll,
  hasMorePages,
  filters,
  setFilters,
  selectOptionsWithoutInteractions,
  selectOptionsWithoutSales,
  filtersDropdownOpen,
  setFiltersDropdownOpen,
  salesDropdownOpen,
  setSalesDropdownOpen,
  channelDropdownOpen,
  setChannelDropdownOpen,
  dropdownOpen,
  setDropdownOpen,
  tagDropdownOpen,
  setTagDropdownOpen,
  interactiveButtons,
  filteredQuickMessages,
  isChannelSupported,
  isDarkMode,
}) {
  const dispatch = useDispatch();

  const settings = useSelector(
    (state) => state.chat?.config?.general?.settings
  );

  const user = useSelector((state) => state.chat?.config?.user);
  // Função para obter a cor correta do ícone baseada no tipo do canal
  const getChannelIconColor = (channel) => {
    if (!channel) return 'var(--whatsapp-color)';

    if (channel.icon_color) {
      return channel.icon_color;
    }

    const channelType = getTypeChannel(channel);
    switch (channelType) {
      case 'waba':
      case 'zapi':
        return 'var(--whatsapp-color)';
      case 'facebook':
        return '#0084F4';
      case 'instagram':
        return '#FFCA5D';
      case 'email':
        return '#ff665d';
      case 'webchat':
        return '#FFCA5D';
      default:
        return 'var(--whatsapp-color)';
    }
  };

  // Função para obter o ícone correto baseado no tipo do canal
  const getChannelIcon = (channel) => {
    if (!channel) return 'poli-icon pi-whatsapp-fill';

    if (channel.icon) {
      return channel.icon;
    }

    const channelType = getTypeChannel(channel);
    switch (channelType) {
      case 'waba':
        return 'poli-icon pi-whatsapp-api-fill';
      case 'zapi':
        return 'poli-icon pi-whatsapp-fill';
      case 'facebook':
        return 'poli-icon pi-messenger-fill';
      case 'instagram':
        return 'poli-icon pi-instagram-fill';
      case 'email':
        return 'poli-icon pi-mail-fill';
      case 'webchat':
        return 'poli-icon pi-webchat-fill';
      default:
        return 'poli-icon pi-whatsapp-fill';
    }
  };

  // Função para enviar o disparo
  const sendTrigger = async () => {
    try {
      // Verifica se há contatos selecionados
      if (selectedContacts.size === 0) {
        alert('Selecione pelo menos um contato para enviar o disparo.');
        return;
      }

      // Verifica se há canal selecionado
      if (!messageConfig.channel) {
        alert('Selecione um canal para enviar o disparo.');
        return;
      }

      // Verifica se há template ou mensagem personalizada
      if (!messageConfig.template && !messageConfig.customMessage) {
        alert('Selecione um template ou digite uma mensagem personalizada.');
        return;
      }

      // Prepara os dados para envio
      const payload = {
        customer_id: settings?.customer_id || 1,
        entry_type:
          getTypeChannel(messageConfig.channel) === 'zapi'
            ? 'BROKER'
            : getTypeChannel(messageConfig.channel).toUpperCase(),
        entry_channel_id: messageConfig.channel.id,
        entry_user_id: user.id,
        entry_msg:
          messageConfig.customMessage ||
          (messageConfig.template?.originalQuickMessage?.text
            ? JSON.parse(messageConfig.template.originalQuickMessage.text)?.body
            : ''),
        entry_email: user.email,
        entry_create_tag: false,
        entry_deduplicate_contact: true,
        entry_international_phone: false,
        contacts: Array.from(selectedContacts.values()).map((contact) => ({
          contact_id: contact.uuid,
          contact_data: contact,
        })),
        quick_message_id:
          messageConfig.template?.originalQuickMessage?.id || null,
        entry_custom_tag_id: messageConfig.tag ? messageConfig.tag.id : null,
      };

      // Faz a requisição para a API usando o service
      await TriggerSuggestionService.sendTrigger(payload);

      // Limpa os contatos selecionados após envio bem-sucedido
      setSelectedContacts(new Map());

      alert('Disparo enviado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar disparo:', error);
      alert(`Erro ao enviar disparo: ${error.message}`);
    }
  };

  const getTypeChannel = (channel) => {
    // Verifica se o canal existe e tem channel_id
    if (!channel || !channel.channel_id) return 'other';

    // Canais WABA (Business API)
    if (channel.channel_id === 6) return 'waba';

    // Canais Z-API (WhatsApp Business)
    if (channel.channel_id === 1) return 'zapi';

    // pode retornar outros tipos de canais abixo
    return 'other';
  };

  // Função para formatar a mensagem do template
  const formatTemplateMessage = (template, typeChannel) => {
    if (!template) {
      if (typeChannel === 'zapi') {
        return 'Selecione uma mensagem rápida';
      }
      if (typeChannel === 'waba') {
        return 'Selecione um template';
      }
      return 'Selecione um template';
    }

    // Template de mensagem rápida
    if (template.isQuickMessage && template.originalQuickMessage) {
      let text = 'Sem texto';
      try {
        if (template.originalQuickMessage.text) {
          const parsed = JSON.parse(template.originalQuickMessage.text);
          text =
            parsed?.body || template.originalQuickMessage.text || 'Sem texto';
        }
      } catch (e) {
        // Se não for JSON válido, usa o texto direto
        text = template.originalQuickMessage.text || 'Sem texto';
      }

      const tag = template.originalQuickMessage.tag || 'Sem tag';
      return `##${tag} - ${
        typeof text === 'string' ? text.substring(0, 50) : 'Sem texto'
      }`;
    }

    // Template de botão interativo
    if (template.isInteractiveButton) {
      const message = template.message || 'Sem texto';
      return `##${template.key} ${
        typeof message === 'string' ? message.substring(0, 50) : 'Sem texto'
      }`;
    }

    // Template genérico
    if (template.key) {
      return template.key;
    }

    return 'Selecione um template';
  };

  // Função para obter a mensagem de status
  const getStatusMessage = () => {
    if (!messageConfig.channel) {
      return 'Selecione um canal primeiro';
    }

    if (
      typeof isChannelSupported === 'function' &&
      !isChannelSupported(messageConfig.channel)
    ) {
      return 'Canal não suportado';
    }

    return formatTemplateMessage(
      messageConfig.template,
      getTypeChannel(messageConfig?.channel)
    );
  };

  const toggleContactSelection = (contact) => {
    setSelectedContacts((prev) => {
      const newMap = new Map(prev);
      const contactId = contact.uuid;

      if (newMap.has(contactId)) {
        newMap.delete(contactId);
      } else {
        newMap.set(contactId, contact);
      }
      return newMap;
    });
  };

  return (
    <>
      {/* Main Content */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '20px',
          width: '100%',
        }}
        className="var(--primary-background-color)"
      >
        {/* Contact Selection Section */}
        <div
          style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : '400px',
            backgroundColor: isDarkMode
              ? 'var(--primary-background-color)'
              : 'white',
            borderRadius: '5px',
            padding: '12px',
            boxShadow: isDarkMode
              ? '0 2px 4px rgba(255,255,255,0.1)'
              : '0 2px 4px rgba(0,0,0,0.1)',
            border: isDarkMode ? '1px solid var(--border-color)' : 'none',
          }}
        >
          <h2
            style={{
              fontSize: '14px',
              fontWeight: '600',
              margin: '0 0 6px 0',
              color: isDarkMode ? 'var(--primary)' : '#333',
            }}
          >
            Selecione aqui os contatos desejados:
          </h2>

          {/* Filters */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              <label
                style={{
                  fontSize: '12px',
                  color: isDarkMode ? 'var(--secondary-font-color)' : '#666',
                  fontWeight: '500',
                  marginBottom: '2px',
                }}
              >
                Sem interação há
              </label>
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                }}
              >
                {/* Campo de seleção principal */}
                <div
                  onClick={() => setFiltersDropdownOpen(!filtersDropdownOpen)}
                  style={{
                    padding: '8px 12px',
                    border: isDarkMode
                      ? '1px solid var(--border-color)'
                      : '1px solid #ced4da',
                    borderRadius: '4px',
                    backgroundColor: isDarkMode
                      ? 'var(--secondary-background-color)'
                      : '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    minHeight: '38px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span
                    style={{
                      color: isDarkMode ? 'var(--primary)' : '#333',
                    }}
                  >
                    {selectOptionsWithoutInteractions.find(
                      (opt) => opt.value === filters.withoutInteractions
                    )?.label || 'Selecione'}
                  </span>
                  <div
                    style={{
                      width: '0',
                      height: '0',
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '6px solid #6c757d',
                      marginLeft: '8px',
                      flexShrink: 0,
                      transform: filtersDropdownOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                </div>

                {/* Dropdown de opções */}
                {filtersDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: isDarkMode
                        ? 'var(--primary-background-color)'
                        : '#fff',
                      border: isDarkMode
                        ? '1px solid var(--border-color)'
                        : '1px solid #ced4da',
                      borderRadius: '4px',
                      boxShadow: isDarkMode
                        ? '0 4px 6px rgba(255, 255, 255, 0.1)'
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto',
                    }}
                  >
                    {selectOptionsWithoutInteractions.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => {
                          setFilters({
                            ...filters,
                            withoutInteractions: option.value,
                          });
                          setFiltersDropdownOpen(false);
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: isDarkMode
                            ? '1px solid var(--border-color)'
                            : '1px solid #f0f0f0',
                          fontSize: '12px',
                          backgroundColor:
                            filters.withoutInteractions === option.value
                              ? isDarkMode
                                ? 'var(--secondary-background-color)'
                                : '#f8f9fa'
                              : isDarkMode
                              ? 'var(--primary-background-color)'
                              : '#fff',
                          color: isDarkMode
                            ? 'var(--primary-font-color)'
                            : '#333',
                        }}
                        onMouseEnter={(e) => {
                          if (filters.withoutInteractions !== option.value) {
                            e.target.style.backgroundColor = isDarkMode
                              ? 'var(--secondary-background-color)'
                              : '#f8f9fa';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (filters.withoutInteractions !== option.value) {
                            e.target.style.backgroundColor = isDarkMode
                              ? 'var(--primary-background-color)'
                              : '#fff';
                          }
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              <label
                style={{
                  fontSize: '12px',
                  color: isDarkMode ? 'var(--secondary-font-color)' : '#666',
                  fontWeight: '500',
                  marginBottom: '2px',
                }}
              >
                Sem venda há
              </label>

              <div
                style={{
                  position: 'relative',
                  width: '100%',
                }}
              >
                {/* Campo de seleção principal */}
                <div
                  onClick={() => setSalesDropdownOpen(!salesDropdownOpen)}
                  style={{
                    padding: '8px 12px',
                    border: isDarkMode
                      ? '1px solid var(--border-color)'
                      : '1px solid #ced4da',
                    borderRadius: '4px',
                    backgroundColor: isDarkMode
                      ? 'var(--secondary-background-color)'
                      : '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    minHeight: '38px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span
                    style={{
                      color: isDarkMode ? 'var(--primary)' : '#333',
                    }}
                  >
                    {selectOptionsWithoutSales.find(
                      (opt) => opt.value === filters.withoutSales
                    )?.label || 'Selecione'}
                  </span>
                  <div
                    style={{
                      width: '0',
                      height: '0',
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '6px solid #6c757d',
                      marginLeft: '8px',
                      flexShrink: 0,
                      transform: salesDropdownOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                </div>

                {/* Dropdown de opções */}
                {salesDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: isDarkMode
                        ? 'var(--primary-background-color)'
                        : '#fff',
                      border: isDarkMode
                        ? '1px solid var(--border-color)'
                        : '1px solid #ced4da',
                      borderRadius: '4px',
                      boxShadow: isDarkMode
                        ? '0 4px 6px rgba(255, 255, 255, 0.1)'
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto',
                    }}
                  >
                    {selectOptionsWithoutSales.map((option) => (
                      <div
                        key={option.id}
                        onClick={() => {
                          setFilters({
                            ...filters,
                            withoutSales: option.value,
                          });
                          setSalesDropdownOpen(false);
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: isDarkMode
                            ? '1px solid var(--border-color)'
                            : '1px solid #f0f0f0',
                          fontSize: '12px',
                          backgroundColor:
                            filters.withoutSales === option.value
                              ? isDarkMode
                                ? 'var(--secondary-background-color)'
                                : '#f8f9fa'
                              : isDarkMode
                              ? 'var(--primary-background-color)'
                              : '#fff',
                          color: isDarkMode
                            ? 'var(--primary-font-color)'
                            : '#333',
                        }}
                        onMouseEnter={(e) => {
                          if (filters.withoutSales !== option.value) {
                            e.target.style.backgroundColor = isDarkMode
                              ? 'var(--secondary-background-color)'
                              : '#f8f9fa';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (filters.withoutSales !== option.value) {
                            e.target.style.backgroundColor = isDarkMode
                              ? 'var(--primary-background-color)'
                              : '#fff';
                          }
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Suggested Contacts */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '10px',
            }}
          >
            <h3
              style={{
                fontSize: '13px',
                fontWeight: '600',
                color: isDarkMode ? 'var(--primary)' : '#333',
                margin: '0',
              }}
            >
              {contactsLoading
                ? 'Carregando...'
                : `${contactsPagination.total} contatos sugeridos:`}
            </h3>
          </div>

          {/* Contact List */}
          <div
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
            }}
            onScroll={handleScroll}
          >
            {contactsLoading && suggestedContacts.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '20px',
                  color: isDarkMode ? 'var(--secondary-font-color)' : '#666',
                  fontSize: '12px',
                }}
              >
                Carregando contatos...
              </div>
            ) : (
              <>
                {suggestedContacts.map((contact) => (
                  <div
                    key={contact.uuid}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      borderBottom: isDarkMode
                        ? '1px solid var(--border-color)'
                        : '1px solid #f0f0f0',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleContactSelection(contact)}
                  >
                    {/* Checkbox */}
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: isDarkMode
                          ? 'var(--secondary-background-color)'
                          : '#e9ecef',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: isDarkMode
                          ? '2px solid var(--border-color)'
                          : '2px solid #ddd',
                      }}
                    >
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: selectedContacts.has(contact.uuid)
                            ? isDarkMode
                              ? 'var(--activated-icons-color)'
                              : '#007bff'
                            : isDarkMode
                            ? 'var(--primary-background-color)'
                            : 'white',
                          borderRadius: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '12px',
                          border: selectedContacts.has(contact.uuid)
                            ? 'none'
                            : '1px solid #ddd',
                          cursor: 'pointer',
                        }}
                      >
                        {selectedContacts.has(contact.uuid) ? '✓' : ''}
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div
                      style={{
                        flex: '1',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '500',
                          color: isDarkMode
                            ? 'var(--primary-font-color)'
                            : '#333',
                          marginBottom: '2px',
                        }}
                      >
                        {contact.attributes.name}
                      </div>
                      <div
                        style={{
                          fontSize: '10px',
                          color: isDarkMode
                            ? 'var(--secondary-font-color)'
                            : '#666',
                          marginBottom: '2px',
                        }}
                      >
                        {contact.attributes.phone}
                      </div>
                      <div
                        style={{
                          fontSize: '10px',
                          color: isDarkMode
                            ? 'var(--activated-icons-color)'
                            : '#007bff',
                        }}
                      >
                        {contact.attributes.origin_category || 'Sem categoria'}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Indicador de carregamento para mais páginas */}
                {contactsLoading && suggestedContacts.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '12px',
                      color: isDarkMode
                        ? 'var(--secondary-font-color)'
                        : '#666',
                      fontSize: '10px',
                      fontStyle: 'italic',
                    }}
                  >
                    Carregando mais contatos...
                  </div>
                )}

                {/* Indicador de fim da lista */}
                {!hasMorePages && suggestedContacts.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '12px',
                      color: isDarkMode ? 'var(--tertiary-font-color)' : '#999',
                      fontSize: '10px',
                      fontStyle: 'italic',
                    }}
                  >
                    Todos os contatos foram carregados
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Message Definition Section */}
        <div
          style={{
            width: '100%',
            backgroundColor: isDarkMode
              ? 'var(--primary-background-color)'
              : 'white',
            borderRadius: '5px',
            padding: '12px',
            boxShadow: isDarkMode
              ? '0 2px 4px rgba(255,255,255,0.1)'
              : '0 2px 4px rgba(0,0,0,0.1)',
            border: isDarkMode ? '1px solid var(--border-color)' : 'none',
          }}
        >
          <h2
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: isDarkMode ? 'var(--primary-font-color)' : '#333',
              margin: '0 0 6px 0',
              width: '100%',
              maxWidth: '100%',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            Defina o canal e a mensagem que será enviada:
          </h2>

          {/* Form Fields */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                width: '100%',
              }}
            >
              <label
                style={{
                  fontSize: '12px',
                  color: isDarkMode ? 'var(--secondary-font-color)' : '#666',
                  fontWeight: '500',
                  marginBottom: '2px',
                }}
              >
                Selecione o canal
              </label>
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                }}
              >
                {/* Campo de seleção principal */}
                <div
                  onClick={() => {
                    setChannelDropdownOpen(!channelDropdownOpen);
                    setDropdownOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    border: isDarkMode
                      ? '1px solid var(--border-color)'
                      : '1px solid #ced4da',
                    borderRadius: '4px',
                    backgroundColor: isDarkMode
                      ? 'var(--secondary-background-color)'
                      : '#fff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    minHeight: '38px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span
                    style={{
                      color: messageConfig.channel?.id
                        ? isDarkMode
                          ? 'var(--primary-font-color)'
                          : '#333'
                        : isDarkMode
                        ? 'var(--tertiary-font-color)'
                        : '#6c757d',
                    }}
                  >
                    {messageConfig.channel ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Icon
                          icon={getChannelIcon(messageConfig.channel)}
                          size={16}
                          color={getChannelIconColor(messageConfig.channel)}
                        />
                        <span>{messageConfig.channel.name}</span>
                      </div>
                    ) : (
                      'Selecione um canal'
                    )}
                  </span>
                  <div
                    style={{
                      width: '0',
                      height: '0',
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '6px solid #6c757d',
                      marginLeft: '8px',
                      flexShrink: 0,
                      transform: channelDropdownOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                </div>

                {/* Dropdown de opções */}
                {channelDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: isDarkMode
                        ? 'var(--primary-background-color)'
                        : '#fff',
                      border: isDarkMode
                        ? '1px solid var(--border-color)'
                        : '1px solid #ced4da',
                      borderRadius: '4px',
                      boxShadow: isDarkMode
                        ? '0 4px 6px rgba(255, 255, 255, 0.1)'
                        : '0 4px 6px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      maxHeight: '200px',
                      overflowY: 'auto',
                    }}
                  >
                    {channels?.map((channel) => {
                      const channelType = getTypeChannel(channel);
                      const isSupported =
                        typeof isChannelSupported === 'function'
                          ? isChannelSupported(channel)
                          : false;
                      return (
                        <div
                          key={channel.id}
                          onClick={() => {
                            if (isSupported) {
                              setMessageConfig((prev) => ({
                                ...prev,
                                channel: channel,
                                template: null,
                                customMessage:
                                  getTypeChannel(messageConfig.channel) ===
                                  'waba'
                                    ? ''
                                    : messageConfig.customMessage,
                              }));
                              setCurrentChannelId(channel?.id);
                              setChannelDropdownOpen(false);
                            }
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: isSupported ? 'pointer' : 'not-allowed',
                            borderBottom: isDarkMode
                              ? '1px solid var(--border-color)'
                              : '1px solid #f0f0f0',
                            fontSize: '12px',
                            backgroundColor: isDarkMode
                              ? 'var(--primary-background-color)'
                              : '#fff',
                            color: isDarkMode
                              ? 'var(--primary-font-color)'
                              : '#333',
                            opacity: isSupported ? 1 : 0.5,
                          }}
                          onMouseEnter={(e) => {
                            if (
                              isSupported &&
                              messageConfig.channel?.id !== channel.id
                            ) {
                              e.target.style.backgroundColor = isDarkMode
                                ? 'var(--secondary-background-color)'
                                : '#f8f9fa';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (
                              isSupported &&
                              messageConfig.channel?.id !== channel.id
                            ) {
                              e.target.style.backgroundColor = isDarkMode
                                ? 'var(--primary-background-color)'
                                : '#fff';
                            }
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <Icon
                              icon={getChannelIcon(channel)}
                              size={16}
                              color={getChannelIconColor(channel)}
                            />
                            <span>{channel.name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                width: '100%',
              }}
            >
              <label
                style={{
                  fontSize: '12px',
                  color: isDarkMode ? 'var(--secondary-font-color)' : '#666',
                  fontWeight: '500',
                  marginBottom: '2px',
                }}
              >
                {typeof messageConfig.channel === 'object' &&
                  getTypeChannel(messageConfig?.channel) === 'waba' &&
                  'Selecione o template'}
                {typeof messageConfig?.channel === 'object' &&
                  getTypeChannel(messageConfig?.channel) === 'zapi' &&
                  'Selecione uma mensagem rápida'}
                {!messageConfig?.channel && 'Selecione o template'}
              </label>

              <div
                style={{
                  position: 'relative',
                  width: '100%',
                }}
              >
                {/* Campo de seleção principal */}
                <div
                  onClick={() => {
                    if (
                      messageConfig.channel &&
                      typeof isChannelSupported === 'function' &&
                      isChannelSupported(messageConfig.channel)
                    ) {
                      setDropdownOpen(!dropdownOpen);
                      setChannelDropdownOpen(false);
                      setTagDropdownOpen(false);
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    border: isDarkMode
                      ? '1px solid var(--border-color)'
                      : '1px solid #ced4da',
                    borderRadius: '4px',
                    backgroundColor: isDarkMode
                      ? 'var(--secondary-background-color)'
                      : '#fff',
                    cursor:
                      messageConfig.channel &&
                      typeof isChannelSupported === 'function' &&
                      isChannelSupported(messageConfig.channel)
                        ? 'pointer'
                        : 'not-allowed',
                    fontSize: '14px',
                    minHeight: '38px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    opacity:
                      messageConfig.channel &&
                      typeof isChannelSupported === 'function' &&
                      isChannelSupported(messageConfig.channel)
                        ? 1
                        : 0.5,
                  }}
                >
                  <span
                    style={{
                      color: messageConfig.template
                        ? isDarkMode
                          ? 'var(--primary-font-color)'
                          : '#333'
                        : isDarkMode
                        ? 'var(--tertiary-font-color)'
                        : '#6c757d',
                    }}
                  >
                    {getStatusMessage()}
                  </span>
                  <div
                    style={{
                      width: '0',
                      height: '0',
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '6px solid #6c757d',
                      marginLeft: '8px',
                      flexShrink: 0,
                      transform: dropdownOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                </div>

                {/* Dropdown de opções */}
                {dropdownOpen &&
                  messageConfig.channel &&
                  typeof isChannelSupported === 'function' &&
                  isChannelSupported(messageConfig.channel) && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: isDarkMode
                          ? 'var(--primary-background-color)'
                          : '#fff',
                        border: isDarkMode
                          ? '1px solid var(--border-color)'
                          : '1px solid #ced4da',
                        borderRadius: '4px',
                        boxShadow: isDarkMode
                          ? '0 4px 6px rgba(255, 255, 255, 0.1)'
                          : '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                      }}
                    >
                      {/* Opções baseadas no tipo de canal */}
                      {getTypeChannel(messageConfig.channel) === 'waba' ? (
                        <>
                          {/* Separador Templates WABA */}
                          <div
                            style={{
                              padding: '8px 12px',
                              fontWeight: 'bold',
                              backgroundColor: isDarkMode
                                ? 'var(--secondary-background-color)'
                                : '#f8f9fa',
                              fontSize: '12px',
                              color: isDarkMode
                                ? 'var(--secondary-font-color)'
                                : '#666',
                            }}
                          >
                            Templates
                          </div>

                          {/* Opções de mensagens rápidas filtradas para WABA */}
                          {filteredQuickMessages &&
                          filteredQuickMessages.length > 0 ? (
                            filteredQuickMessages.map((quickMessage) => {
                              let textBody = '';
                              try {
                                if (quickMessage.text) {
                                  // Tenta fazer parse do JSON, se falhar usa o texto direto
                                  try {
                                    const textObj = JSON.parse(
                                      quickMessage.text
                                    );
                                    textBody = textObj.body || '';
                                  } catch (parseError) {
                                    // Se não for JSON válido, usa o texto direto
                                    textBody = quickMessage.text || '';
                                  }
                                }
                              } catch (e) {
                                textBody = quickMessage.text || '';
                              }

                              const tag = quickMessage.tag || 'Sem tag';

                              return (
                                <div
                                  key={`waba_${quickMessage.id}`}
                                  onClick={() => {
                                    const selectedTemplate = {
                                      id: `waba_${quickMessage.id}`,
                                      key:
                                        tag ||
                                        (typeof textBody === 'string'
                                          ? textBody.substring(0, 30)
                                          : '') ||
                                        `Mensagem ${quickMessage.id}`,
                                      message: quickMessage.text,
                                      isQuickMessage: true,
                                      originalQuickMessage: quickMessage,
                                    };

                                    setMessageConfig({
                                      ...messageConfig,
                                      template: selectedTemplate,
                                      customMessage: '',
                                    });
                                    setDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: isDarkMode
                                      ? '1px solid var(--border-color)'
                                      : '1px solid #f0f0f0',
                                    display: 'grid',
                                    gridTemplateColumns: '100px 1fr',
                                    gap: '8px',
                                    alignItems: 'center',
                                    fontSize: '12px',
                                    backgroundColor: isDarkMode
                                      ? 'var(--primary-background-color)'
                                      : '#fff',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = isDarkMode
                                      ? 'var(--secondary-background-color)'
                                      : '#f8f9fa';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = isDarkMode
                                      ? 'var(--primary-background-color)'
                                      : '#fff';
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: '500',
                                      color: isDarkMode
                                        ? 'var(--primary-font-color)'
                                        : '#333',
                                    }}
                                  >
                                    ##{tag}
                                  </span>
                                  <span
                                    style={{
                                      color: isDarkMode
                                        ? 'var(--secondary-font-color)'
                                        : '#666',
                                    }}
                                  >
                                    {typeof textBody === 'string'
                                      ? textBody.substring(0, 50)
                                      : 'Sem texto'}
                                  </span>
                                </div>
                              );
                            })
                          ) : (
                            <div
                              style={{
                                padding: '8px 12px',
                                color: isDarkMode
                                  ? 'var(--tertiary-font-color)'
                                  : '#6c757d',
                                fontStyle: 'italic',
                              }}
                            >
                              {typeof messageConfig.channel === 'object' &&
                                getTypeChannel(messageConfig?.channel) ===
                                  'waba' &&
                                'Nenhum template disponível'}
                              {typeof messageConfig?.channel === 'object' &&
                                getTypeChannel(messageConfig?.channel) ===
                                  'zapi' &&
                                'Nenhuma mensagem rápida disponível'}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Separador Botões Interativos Z-API */}
                          {/* <div
                            style={{
                              padding: '8px 12px',
                              fontWeight: 'bold',
                              backgroundColor: '#f8f9fa',
                              fontSize: '12px',
                              color: '#666',
                            }}
                          >
                            Botão Interativo
                          </div> */}

                          {/* Opções de botões interativos */}
                          {interactiveButtons.map((button) => (
                            <div
                              key={button.id}
                              onClick={() => {
                                const selectedTemplate = {
                                  id: button.id,
                                  key: button.key,
                                  message: button.message,
                                  isInteractiveButton: true,
                                };

                                setMessageConfig({
                                  ...messageConfig,
                                  template: selectedTemplate,
                                  customMessage: '',
                                });
                                setDropdownOpen(false);
                              }}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: isDarkMode
                                  ? '1px solid var(--border-color)'
                                  : '1px solid #f0f0f0',
                                fontSize: '12px',
                                backgroundColor: isDarkMode
                                  ? 'var(--primary-background-color)'
                                  : '#fff',
                                color: isDarkMode
                                  ? 'var(--primary-font-color)'
                                  : '#333',
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = isDarkMode
                                  ? 'var(--secondary-background-color)'
                                  : '#f8f9fa';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = isDarkMode
                                  ? 'var(--primary-background-color)'
                                  : '#fff';
                              }}
                            >
                              {button.key}
                            </div>
                          ))}

                          {/* Separador Mensagens Rápidas Z-API */}
                          <div
                            style={{
                              padding: '8px 12px',
                              fontWeight: 'bold',
                              backgroundColor: isDarkMode
                                ? 'var(--secondary-background-color)'
                                : '#f8f9fa',
                              fontSize: '12px',
                              color: isDarkMode
                                ? 'var(--secondary-font-color)'
                                : '#666',
                            }}
                          >
                            Mensagens Rápidas
                          </div>

                          {/* Opções de mensagens rápidas filtradas */}
                          {filteredQuickMessages &&
                          filteredQuickMessages.length > 0 ? (
                            filteredQuickMessages.map((quickMessage) => {
                              let textBody = '';
                              try {
                                if (quickMessage.text) {
                                  // Tenta fazer parse do JSON, se falhar usa o texto direto
                                  try {
                                    const textObj = JSON.parse(
                                      quickMessage.text
                                    );
                                    textBody = textObj.body || '';
                                  } catch (parseError) {
                                    // Se não for JSON válido, usa o texto direto
                                    textBody = quickMessage.text || '';
                                  }
                                }
                              } catch (e) {
                                textBody = quickMessage.text || '';
                              }

                              const tag = quickMessage.tag || 'Sem tag';

                              return (
                                <div
                                  key={`quick_${quickMessage.id}`}
                                  onClick={() => {
                                    const selectedTemplate = {
                                      id: `quick_${quickMessage.id}`,
                                      key:
                                        tag ||
                                        (typeof textBody === 'string'
                                          ? textBody.substring(0, 30)
                                          : '') ||
                                        `Mensagem ${quickMessage.id}`,
                                      message: quickMessage.text,
                                      isQuickMessage: true,
                                      originalQuickMessage: quickMessage,
                                    };

                                    setMessageConfig({
                                      ...messageConfig,
                                      template: selectedTemplate,
                                      customMessage: '',
                                    });
                                    setDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: isDarkMode
                                      ? '1px solid var(--border-color)'
                                      : '1px solid #f0f0f0',
                                    display: 'grid',
                                    gridTemplateColumns: '160px 1fr',
                                    gap: '8px',
                                    alignItems: 'center',
                                    fontSize: '12px',
                                    backgroundColor: isDarkMode
                                      ? 'var(--primary-background-color)'
                                      : '#fff',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = isDarkMode
                                      ? 'var(--secondary-background-color)'
                                      : '#f8f9fa';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = isDarkMode
                                      ? 'var(--primary-background-color)'
                                      : '#fff';
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: '500',
                                      color: isDarkMode
                                        ? 'var(--primary-font-color)'
                                        : '#333',
                                    }}
                                  >
                                    {tag}
                                  </span>
                                  <span
                                    style={{
                                      color: isDarkMode ? '#999' : '#666',
                                    }}
                                  >
                                    {typeof textBody === 'string'
                                      ? textBody.substring(0, 50)
                                      : 'Sem texto'}
                                  </span>
                                </div>
                              );
                            })
                          ) : (
                            <div
                              style={{
                                padding: '8px 12px',
                                color: isDarkMode
                                  ? 'var(--tertiary-font-color)'
                                  : '#6c757d',
                                fontStyle: 'italic',
                              }}
                            >
                              Nenhuma mensagem rápida disponível
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
              </div>

              {/* Campo de texto livre para Z-API */}
              {messageConfig.channel &&
                getTypeChannel(messageConfig.channel) === 'zapi' && (
                  <div
                    style={{
                      marginTop: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <label
                      style={{
                        fontSize: '12px',
                        color: isDarkMode
                          ? 'var(--secondary-font-color)'
                          : '#666',
                        fontWeight: '500',
                        marginBottom: '2px',
                      }}
                    >
                      Mensagem
                    </label>
                    <textarea
                      placeholder="Digite sua mensagem aqui..."
                      value={messageConfig.customMessage || ''}
                      onChange={(e) =>
                        setMessageConfig({
                          ...messageConfig,
                          customMessage: e.target.value,
                        })
                      }
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '8px',
                        border: isDarkMode
                          ? '1px solid var(--border-color)'
                          : '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        backgroundColor: isDarkMode
                          ? 'var(--secondary-background-color)'
                          : '#fff',
                        color: isDarkMode
                          ? 'var(--primary-font-color)'
                          : '#333',
                      }}
                    />
                  </div>
                )}
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                width: '100%',
              }}
            >
              <label
                style={{
                  fontSize: '12px',
                  color: isDarkMode ? 'var(--secondary-font-color)' : '#666',
                  fontWeight: '500',
                  marginBottom: '2px',
                }}
              >
                Etiqueta
              </label>
              <TagSelector
                selectedTag={messageConfig.tag}
                onTagSelect={(tag) => {
                  setMessageConfig({ ...messageConfig, tag: tag });
                }}
                placeholder="Selecione uma etiqueta"
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '20px',
          padding: '12px',
          backgroundColor: isDarkMode
            ? 'var(--primary-background-color)'
            : 'white',
          borderRadius: '5px',
          boxShadow: isDarkMode
            ? '0 2px 4px rgba(255,255,255,0.1)'
            : '0 2px 4px rgba(0,0,0,0.1)',
          border: isDarkMode ? '1px solid var(--border-color)' : 'none',
        }}
      >
        <button
          style={{
            padding: '8px 16px',
            backgroundColor: isDarkMode
              ? 'var(--secondary-background-color)'
              : '#007bff',
            color: isDarkMode ? 'var(--primary-font-color)' : 'white',
            border: isDarkMode ? '1px solid var(--border-color)' : 'none',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
          onClick={() => {
            console.log('clearFilter e openChatTab');
            dispatch(ChatActions.clearFilter());
            dispatch(ChatActions.openChatTab());
          }}
        >
          Cancelar
        </button>

        <button
          style={{
            padding: '8px 16px',
            backgroundColor:
              (messageConfig.template || messageConfig.customMessage) &&
              selectedContacts.size > 0
                ? isDarkMode
                  ? 'var(--activated-icons-color)'
                  : '#28a745'
                : isDarkMode
                ? 'var(--secondary-background-color)'
                : '#e9ecef',
            color:
              (messageConfig.template || messageConfig.customMessage) &&
              selectedContacts.size > 0
                ? isDarkMode
                  ? 'var(--white-font-color)'
                  : 'white'
                : isDarkMode
                ? 'var(--tertiary-font-color)'
                : '#6c757d',
            border: isDarkMode ? '1px solid var(--border-color)' : 'none',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            cursor:
              (messageConfig.template || messageConfig.customMessage) &&
              selectedContacts.size > 0
                ? 'pointer'
                : 'not-allowed',
          }}
          disabled={
            (!messageConfig.template && !messageConfig.customMessage) ||
            selectedContacts.size === 0
          }
          onClick={sendTrigger}
        >
          Enviar disparo
        </button>
      </div>
    </>
  );
}

export default WhatsappMarketingContent;
